import React, { Component } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthUserContext } from '../Session';
import { withFirebase } from '../Firebase';
import { ProjectSelectionContext } from '../ProjectSelection';
import MessageList from './MessageList';
import './Messages.css';

const withTranslation = (Component) => {
  return (props) => {
    const { t } = useTranslation();
    return <Component {...props} t={t} />;
  };
};

class Messages extends Component {
  constructor(props) {
    super(props);

    this.state = {
      text: '',
      loading: false,
      messages: [],
      activityLogs: [],
      limit: 20,
      selectedOrganisation: 'PUBLIC',
      showActivityLogs: true,
      activitySearch: '',
      activityFilter: 'all',
      activityLogsLimit: 20,
    };
  }

  componentDidMount() {
    this.onListenForMessages();
  }

  fetchFilteredActivityLogs = (authUser, selectedProject) => {
    if (!authUser || !authUser.roles) return;

    const { activityFilter, activityLogsLimit } = this.state;
    const organizations = Object.keys(authUser.roles).filter(k => authUser.roles[k] !== null);
    if (organizations.length === 0) return;

    const filterParams = {
      projectName: selectedProject || undefined,
      action: activityFilter !== 'all' ? activityFilter : undefined,
      limit: activityLogsLimit,
    };

    Promise.all(organizations.map(org =>
      this.props.firebase.doGetOrgActivityLogFiltered(org, filterParams)
        .then(logs => logs.map(l => ({ ...l, organization: org })))
        .catch(() => [])
    ))
      .then(results => {
        const combined = results.flat().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        this.setState({ activityLogs: combined });
      });
  };

  onChangeActivityLimit = (newLimit) => {
    this.setState({ activityLogsLimit: parseInt(newLimit, 10) }, () => {
      this._lastFetchKey = null;
    });
  };

  onListenForMessages = () => {
    this.setState({ loading: true });

    this.props.firebase.messages().off();

    this.props.firebase
      .messages()
      .orderByChild('createdAt')
      .limitToLast(this.state.limit)
      .on('value', snapshot => {
        const messageObject = snapshot.val();

        if (messageObject) {
          const messageList = Object.keys(messageObject).map(key => ({
            ...messageObject[key],
            uid: key,
          }));

          this.setState({
            messages: messageList,
            loading: false,
          });
        } else {
          this.setState({ messages: [], loading: false });
        }
      });
  };

  componentWillUnmount() {
    this.props.firebase.messages().off();
  }

  onChangeText = event => {
    this.setState({ text: event.target.value });
  };

  onCreateMessage = (event, authUser) => {
    const { text, selectedOrganisation } = this.state;

    const messageData = {
      text: this.state.text,
      userId: authUser.uid,
      username: authUser.username,
      userPhoto: authUser.providerData[0].photoURL,
      createdAt: this.props.firebase.serverValue.TIMESTAMP,
    };

    if (selectedOrganisation !== 'PUBLIC') {
      messageData.organisation = selectedOrganisation;
    }

    this.props.firebase.messages().push(messageData);

    if (selectedOrganisation !== 'PUBLIC') {
      this.props.firebase.maybeLogOrgActivity(selectedOrganisation, {
        action: 'MESSAGE_SENT',
        actorUid: authUser.uid,
        actorEmail: authUser.email || authUser.providerData?.[0]?.email || null,
        details: { text: text.substring(0, 100) },
      }).catch(() => {});
    }

    this.setState({ text: '' });

    event.preventDefault();
  };

  onEditMessage = (message, text, authUser) => {
    const { uid, ...messageSnapshot } = message;

    this.props.firebase.message(message.uid).set({
      ...messageSnapshot,
      text,
      editedAt: this.props.firebase.serverValue.TIMESTAMP,
    });

    if (message.organisation && authUser) {
      this.props.firebase.maybeLogOrgActivity(message.organisation, {
        action: 'MESSAGE_EDITED',
        actorUid: authUser.uid,
        actorEmail: authUser.email || authUser.providerData?.[0]?.email || null,
        details: { text: text.substring(0, 100) },
      }).catch(() => {});
    }
  };

  onRemoveMessage = (uid, authUser) => {
    const messageRef = this.props.firebase.message(uid);
    messageRef.once('value').then(snapshot => {
      const message = snapshot.val();
      messageRef.remove();
      if (message?.organisation && authUser) {
        this.props.firebase.maybeLogOrgActivity(message.organisation, {
          action: 'MESSAGE_DELETED',
          actorUid: authUser.uid,
          actorEmail: authUser.email || authUser.providerData?.[0]?.email || null,
          details: {},
        }).catch(() => {});
      }
    });
  };

  onNextPage = () => {
    this.setState(
      state => ({ limit: state.limit + 5 }),
      this.onListenForMessages,
    );
  };

  render() {
    const { text, messages, activityLogs, loading, selectedOrganisation, showActivityLogs, activitySearch, activityFilter, activityLogsLimit } = this.state;
    const { t } = this.props;

    const actionOptions = Array.from(new Set(activityLogs.map(l => l.action).filter(Boolean))).sort();

    return (
      <AuthUserContext.Consumer>
        {authUser => (
          <ProjectSelectionContext.Consumer>
            {({ selectedProject }) => {
              if (authUser && showActivityLogs) {
                const fetchKey = `${selectedProject || ''}|${activityFilter}|${activityLogsLimit}`;
                if (this._lastFetchKey !== fetchKey) {
                  this._lastFetchKey = fetchKey;
                  setTimeout(() => this.fetchFilteredActivityLogs(authUser, selectedProject), 0);
                }
              }

              const getFilteredMessages = () => {
                if (selectedOrganisation === 'PUBLIC') {
                  const authUserOrgs =
                    authUser && authUser.roles
                      ? Object.keys(authUser.roles).filter(
                        key => authUser.roles[key] !== null,
                      )
                      : [];

                  return messages.filter(message => {
                    if (!message.organisation) return true;
                    return authUserOrgs.includes(message.organisation);
                  });
                }

                return messages.filter(message => message.organisation === selectedOrganisation);
              };

              const getFilteredActivityLogs = () => {
                if (!showActivityLogs) return [];

                let filtered = activityLogs;

                if (selectedOrganisation !== 'PUBLIC') {
                  filtered = filtered.filter(log => log.organization === selectedOrganisation);
                }

                if (activityFilter !== 'all') {
                  filtered = filtered.filter(log => log.action === activityFilter);
                }

                if (activitySearch) {
                  const q = activitySearch.toLowerCase();
                  filtered = filtered.filter(log => {
                    const actionMatch = log.action && log.action.toLowerCase().includes(q);
                    const actorMatch = log.actorEmail && log.actorEmail.toLowerCase().includes(q);
                    const projectMatch = log.details && log.details.projectName && log.details.projectName.toLowerCase().includes(q);
                    return actionMatch || actorMatch || projectMatch;
                  });
                }

                return filtered
                  .map(log => ({
                    ...log,
                    isActivityLog: true,
                    createdAt: log.timestamp,
                  }))
                  .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
                  .slice(0, activityLogsLimit);
              };

              const filteredMessages = getFilteredMessages();
              const filteredLogs = getFilteredActivityLogs();

              const combinedFeed = [...filteredMessages, ...filteredLogs].sort(
                (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
              );

              const canPost = !!authUser;

              return (
                <div>
                  <header>
                    {loading && <div>{t('messages.loading')}</div>}

                    {canPost && !loading && (
                      <div className="message-post-container">
                        <h6><span>💬</span> {t('messages.new_message', 'New Message')}</h6>
                        <form onSubmit={event => this.onCreateMessage(event, authUser)}>
                          <div className="message-form-group">
                            <label htmlFor="organisation-select">{t('messages.post_to_label')}</label>
                            <select
                              id="organisation-select"
                              value={selectedOrganisation}
                              onChange={e => this.setState({ selectedOrganisation: e.target.value })}
                            >
                              <option value="PUBLIC">{t('messages.public_option')}</option>
                              {authUser.roles &&
                                Object.keys(authUser.roles)
                                  .filter(key => authUser.roles[key] !== null)
                                  .map(org => (
                                    <option key={org} value={org}>
                                      {org}
                                    </option>
                                  ))}
                            </select>
                          </div>
                          <textarea
                            value={text}
                            onChange={this.onChangeText}
                            placeholder={t('messages.message_placeholder', 'What\'s on your mind?')}
                          />
                          <div className="message-actions">
                            {filteredMessages.length > 0 && (
                              <button
                                type="button"
                                className="load-more-btn"
                                title={t('messages.load_more_button_title')}
                                onClick={this.onNextPage}
                              >
                                🔄
                              </button>
                            )}
                            <button type="submit">{t('messages.send_button')}</button>
                          </div>
                        </form>
                      </div>
                    )}

                    {authUser && (
                      <div className="message-filter-controls">
                        <div className="message-filter-row">
                          <label className="show-activity-label">
                            <input
                              type="checkbox"
                              checked={showActivityLogs}
                              onChange={e => this.setState({ showActivityLogs: e.target.checked })}
                            />
                            <span>{t('messages.show_activity_logs', 'Show activity logs')}</span>
                          </label>
                          <span className="activity-count-badge">
                            {filteredLogs.length} events
                          </span>
                        </div>

                        {showActivityLogs && (
                          <div className="activity-filter-grid">
                            <input
                              type="search"
                              placeholder={t('admin.search_placeholder', 'Search activity...')}
                              value={activitySearch}
                              onChange={e => this.setState({ activitySearch: e.target.value })}
                              className="activity-search-input"
                            />
                            <div className="activity-select-group">
                              <select
                                value={activityFilter}
                                onChange={e => {
                                  this.setState({ activityFilter: e.target.value }, () => {
                                    this._lastFetchKey = null;
                                  });
                                }}
                                className="activity-filter-select"
                              >
                                <option value="all">{t('messages.all_actions', 'All Actions')}</option>
                                {actionOptions.map(a => (
                                  <option key={a} value={a}>
                                    {a.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={activityLogsLimit}
                                onChange={e => this.onChangeActivityLimit(e.target.value)}
                                className="activity-limit-select"
                                title={t('messages.limit_tooltip', 'Logs per organization')}
                              >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <MessageList
                      authUser={authUser}
                      messages={combinedFeed}
                      onEditMessage={this.onEditMessage}
                      onRemoveMessage={this.onRemoveMessage}
                    />

                    {!loading && combinedFeed.length === 0 && (
                      <p>{t('messages.no_messages')}</p>
                    )}
                  </header>
                </div>
              );
            }}
          </ProjectSelectionContext.Consumer>
        )}
      </AuthUserContext.Consumer>
    );
  }
}

export default withTranslation(withFirebase(Messages));
